import Phaser from 'phaser';
import type { RoomDef } from '../../data/schema';
import { CELL_H, CELL_W, COLS, ROWS } from '../../core/map/grid';
import { tilesetKey } from '../assets';

/** Taille maximale d'un morceau de sol (texture dynamique) : 2 048 px, sûr sur tous les GPU WebGL. */
export const CHUNK = 2048;

export interface MapView { destroy(): void; readonly chunks: number; readonly objects: number; }

interface Stamp { key: string; x: number; y: number; flip: boolean; }

/** Couleur de fond d'un atlas de tuiles (`meta.fill` écrit par tools/build-tiles.py), vert moyen par défaut. */
export function fillOf(scene: Phaser.Scene, atlas: string): number {
  // Phaser copie les clés hors `frames` du JSON de l'atlas dans texture.customData (dont `meta`)
  const meta = (scene.textures.get(atlas).customData as { meta?: { fill?: string } }).meta;
  return meta?.fill ? parseInt(meta.fill.slice(1), 16) : 0x457007;
}

/** Produit de deux couleurs 0xrrggbb (teinte multiplicative). */
export function tintColor(color: number, tint: number): number {
  const ch = (c: number, s: number): number => Math.round(((c >> s) & 255) * (((tint >> s) & 255) / 255));
  return (ch(color, 16) << 16) | (ch(color, 8) << 8) | ch(color, 0);
}

/**
 * Salle générée à l'écran : le sol (tuiles de cellule puis couches plates) est dessiné une fois dans des textures
 * dynamiques de 2 048 px au plus ; les objets sont des images triées par y (profondeur = y, comme les entités).
 */
export function buildMapView(scene: Phaser.Scene, room: RoomDef): MapView | null {
  const g = room.grid;
  if (!g?.ground || !g.biome) return null;
  const atlas = tilesetKey(g.biome);
  if (!scene.textures.exists(atlas)) return null;
  const tex = scene.textures.get(atlas);
  const stamps: Stamp[] = [];
  let k = 0;
  for (let r = -1; r <= ROWS; r++) for (let c = -1; c <= COLS; c++) {
    const t = g.ground[k++]!;
    stamps.push({ key: t.key, x: c * CELL_W + ((r & 1) ? CELL_W : CELL_W / 2), y: r * CELL_H / 2 + CELL_H / 2, flip: t.flip });
  }
  const tint = g.tint ? parseInt(g.tint.slice(1), 16) : 0xffffff;
  for (const d of g.decals ?? []) stamps.push(d);
  const created: { destroy(): void }[] = [];
  const keys: string[] = [];
  let chunks = 0;
  for (let cy = 0; cy < room.height; cy += CHUNK) for (let cx = 0; cx < room.width; cx += CHUNK) {
    const w = Math.ceil(Math.min(CHUNK, room.width - cx)), h = Math.ceil(Math.min(CHUNK, room.height - cy));
    const key = `map:${room.id}:${chunks++}`;
    if (scene.textures.exists(key)) scene.textures.remove(key);
    const dt = scene.textures.addDynamicTexture(key, w, h);
    if (!dt) continue;
    // fond = couleur moyenne du sol (meta.fill de l'atlas, teintée) : les tuiles de sol sont extrudées de 2 px dans
    // l'atlas et se recouvrent, aucun joint sombre ne subsiste au filtrage bilinéaire
    dt.fill(tintColor(fillOf(scene, atlas), tint));
    for (let n = 0; n < stamps.length; n++) {
      const s = stamps[n]!;
      const f = tex.get(s.key);
      if (!f || f.name === '__BASE') continue;
      const px = f.customPivot ? f.pivotX : 0.5, py = f.customPivot ? f.pivotY : 0.5;
      // rejet rapide des tuiles hors du morceau
      const left = s.x - (s.flip ? 1 - px : px) * f.width, top = s.y - py * f.height;
      if (left > cx + w || top > cy + h || left + f.width < cx || top + f.height < cy) continue;
      // positions entières : un tampon à mi-pixel estomperait les bords ; le miroir se fait autour du pivot (centre de cellule)
      dt.stamp(atlas, s.key, Math.round(s.x - cx), Math.round(s.y - cy), { originX: px, originY: py, scaleX: s.flip ? -1 : 1, scaleY: 1, tint });
    }
    dt.render();
    keys.push(key);
    created.push(scene.add.image(cx, cy, key).setOrigin(0, 0).setDepth(-100000));
  }
  for (const o of g.objects ?? []) {
    const f = tex.get(o.key);
    if (!f || f.name === '__BASE') continue;
    const px = f.customPivot ? f.pivotX : 0.5, py = f.customPivot ? f.pivotY : 1;
    created.push(scene.add.image(o.x, o.y, atlas, o.key).setOrigin(px, py).setFlipX(o.flip).setDepth(o.y));
  }
  return {
    chunks, objects: (g.objects ?? []).length,
    destroy() {
      for (const c of created) c.destroy();
      for (const key of keys) if (scene.textures.exists(key)) scene.textures.remove(key);
    },
  };
}
